import { isAxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";

export function useExpertStatusActions() {
  const api = useApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();

  const invalidateExperts = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "experts"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "expert"] });
  };

  const activateApplication = useMutation({
    mutationFn: async (expertId: number) => {
      return await api.admin.expertManager.activateApplication({
        params: { expertId },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Application activated successfully", type: "success" });
      invalidateExperts();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to activate: ${error.message}`, type: "error" });
    },
  });

  const publishExpert = useMutation({
    mutationFn: async (expertId: number) => {
      return await api.admin.expertManager.updateStatus({
        params: { expertId },
        body: { action: "publish" },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Expert published successfully", type: "success" });
      invalidateExperts();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to publish: ${error.message}`, type: "error" });
    },
  });

  const unpublishExpert = useMutation({
    mutationFn: async (expertId: number) => {
      return await api.admin.expertManager.updateStatus({
        params: { expertId },
        body: { action: "unpublish" },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Expert unpublished", type: "success" });
      invalidateExperts();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to unpublish: ${error.message}`, type: "error" });
    },
  });

  const setDraft = useMutation({
    mutationFn: async (expertId: number) => {
      return await api.admin.expertManager.updateStatus({
        params: { expertId },
        body: { action: "set_draft" },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Expert set to draft", type: "success" });
      invalidateExperts();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to set draft: ${error.message}`, type: "error" });
    },
  });

  const setApplication = useMutation({
    mutationFn: async (expertId: number) => {
      return await api.admin.expertManager.updateStatus({
        params: { expertId },
        body: { action: "set_application" },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Expert set to application", type: "success" });
      invalidateExperts();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to set application: ${error.message}`, type: "error" });
    },
  });

  const deleteExpert = useMutation({
    mutationFn: async (expertId: number) => {
      return await api.admin.expertManager.deleteExpert({
        params: { expertId },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Expert account deleted successfully", type: "success" });
      invalidateExperts();
    },
    onError: (error: Error) => {
      const message = isAxiosError(error) && typeof error.response?.data?.message === "string"
        ? error.response.data.message
        : error.message;
      toaster.create({ title: `Failed to delete expert: ${message}`, type: "error" });
    },
  });

  const resendActivation = useMutation({
    mutationFn: async (expertId: number) => {
      return await api.admin.expertManager.resendActivation({
        params: { expertId },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Activation email sent", type: "success" });
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to send email: ${error.message}`, type: "error" });
    },
  });

  return {
    activateApplication,
    publishExpert,
    unpublishExpert,
    setDraft,
    setApplication,
    deleteExpert,
    resendActivation,
  };
}
